// locale store（3 语切换）
// 注意：localStorage 访问必须 client-only（vite-ssg SSR 期间不存在）

import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { i18n } from '@/i18n'
import { STORAGE_KEY, DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from '@/config'

export const useLocaleStore = defineStore('locale', () => {
  // 初始值跟 i18n.global.locale 对齐（i18n 模块加载时已从 localStorage 推断过）
  const initial: SupportedLocale = (() => {
    if (typeof i18n === 'undefined' || !i18n.global) return DEFAULT_LOCALE
    const l = String(i18n.global.locale.value) as SupportedLocale
    return (SUPPORTED_LOCALES as readonly string[]).includes(l) ? l : DEFAULT_LOCALE
  })()
  const current = ref<SupportedLocale>(initial)

  // 验证 locale 是否在白名单
  function isSupported(locale: string): locale is SupportedLocale {
    return (SUPPORTED_LOCALES as readonly string[]).includes(locale)
  }

  // 立即同步 i18n.global.locale + <html lang> + localStorage
  // 不依赖 watch，直接在 setLocale / hydrate 里写，避免 watch 链路失效
  function syncI18n(next: SupportedLocale) {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = next
      try {
        localStorage.setItem(STORAGE_KEY.locale, next)
      } catch {
        // localStorage 写入失败（隐私模式 / 配额满）→ 不影响 UI
      }
    }
    if (i18n && i18n.global) {
      i18n.global.locale.value = next
    }
  }

  // 设置 locale（用户主动切换时调用）
  function setLocale(next: SupportedLocale) {
    if (current.value === next) return
    current.value = next
    syncI18n(next)
  }

  // 从 URL ?lang= 读取
  function hydrateFromUrl() {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    const lang = url.searchParams.get('lang')
    if (lang && isSupported(lang)) {
      current.value = lang
      syncI18n(lang)
    }
  }

  // 从 localStorage 读取（client-only）
  // 返回 true 表示有有效存储值（即用户之前明确选过）
  function hydrateFromStorage(): boolean {
    if (typeof window === 'undefined') return false
    const saved = localStorage.getItem(STORAGE_KEY.locale)
    if (saved && isSupported(saved)) {
      if (saved !== current.value) {
        current.value = saved
        syncI18n(saved)
      }
      return true
    }
    return false
  }

  // 从浏览器语言推断
  function hydrateFromBrowser() {
    if (typeof window === 'undefined') return
    const browser = navigator.language
    if (isSupported(browser)) {
      if (browser !== current.value) {
        current.value = browser
        syncI18n(browser)
      }
      return
    }
    const lower = browser.toLowerCase()
    let detected: SupportedLocale | null = null
    if (lower.startsWith('zh-tw') || lower === 'zh-hk' || lower === 'zh-mo') {
      detected = 'zh-TW'
    } else if (lower.startsWith('zh')) {
      detected = 'zh-CN'
    } else if (lower.startsWith('en')) {
      detected = 'en'
    }
    if (detected && detected !== current.value) {
      current.value = detected
      syncI18n(detected)
    }
  }

  function hydrate() {
    if (typeof window === 'undefined') return
    hydrateFromUrl()
    const hadStoredValue = hydrateFromStorage()
    // 只有 localStorage 没有值时（首次访问），才用浏览器语言兜底
    if (!hadStoredValue) {
      hydrateFromBrowser()
    }
  }

  // 兜底 watch：万一 setLocale/hydrate 漏写，watch 兜底同步
  // 不要 immediate: true
  watch(current, (next) => {
    syncI18n(next)
  })

  return { current, setLocale, hydrate }
})
